import { Button, Grid, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  OrganizationState,
  useOrganizationState,
} from 'features/organizations/state/useOrganizationState';
import { useOrganizationPricing } from 'features/organizations/hooks/useOrganizationPricing';
import { useOrganizationUpgradePreview } from 'features/organizations/hooks/useOrganizationUpgradePreview';
import { useForm } from 'react-hook-form';
import { useOrganizationDetails } from 'features/organizations/hooks/useOrganizationDetails';
import { InlineStatus } from 'components/InlineStatus';
import { formatDate, DATE_ONLY_FORMAT } from 'shared/dates';
import { getOrgCommonSignupFormsValidationSchema } from 'features/organizations/organizations.utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { EmailStatus } from 'shared/getEmailValidationSchema';
import { OrganizationUpgradeSummary } from '../OrganizationUpgradeSummary';
import { OrganizationSeatsFormFields } from '../OrganizationSeatsFormFields';
import { OrganizationSeatsSummary } from '../OrganizationSeatsSummary';

type OrganizationUpgradeSeatsFormValues = {
  regularSeats: number;
  proSeats: number;
};

type OrganizationUpgradeSeatsFormProps = {
  onSubmit: (values: Partial<OrganizationState>) => void;
};

export const OrganizationUpgradeSeatsForm = (
  props: OrganizationUpgradeSeatsFormProps,
): JSX.Element => {
  const { onSubmit } = props;
  const { t } = useTranslation();
  const {
    details,
    isLoading: isDetailsLoading,
    isError: isDetailsError,
  } = useOrganizationDetails();
  const {
    pricing,
    isLoading: isPricingLoading,
    isError: isPricingError,
  } = useOrganizationPricing();
  const organizationState = useOrganizationState();

  const { control, handleSubmit, formState, watch } =
    useForm<OrganizationUpgradeSeatsFormValues>({
      resolver: yupResolver(
        getOrgCommonSignupFormsValidationSchema({
          t,
          requireExistingUserToLogin: false,
          emailValidationCache: new Map<string, EmailStatus>(),
          fields: ['regularSeats', 'proSeats'],
        }),
      ),
      defaultValues: {
        regularSeats: 0,
        proSeats: 0,
      },
      values: {
        regularSeats: organizationState.regularSeats || 0,
        proSeats: organizationState.proSeats || 0,
      },
      mode: 'onBlur',
    });

  const regularSeats = watch('regularSeats');
  const proSeats = watch('proSeats');

  // Subscribe to the same upgrade-preview query that <OrganizationUpgradeSummary />
  // uses so we can disable the Continue button while it's still fetching.
  const { isLoading: isUpgradePreviewLoading, isError: isUpgradePreviewError } =
    useOrganizationUpgradePreview({ regularSeats, proSeats });

  const handleNext = (values: OrganizationUpgradeSeatsFormValues): void => {
    onSubmit(values);
  };

  if (
    !details ||
    isDetailsLoading ||
    isPricingLoading ||
    isDetailsError ||
    isPricingError
  ) {
    return (
      <InlineStatus
        status={isDetailsLoading || isPricingLoading ? 'loading' : 'error'}
      />
    );
  }

  // for updated period
  const currentDate = new Date();
  const displaySubscriptionPeriods = Boolean(details.period);

  const isDisabled =
    isDetailsLoading ||
    isPricingLoading ||
    isUpgradePreviewLoading ||
    isUpgradePreviewError ||
    !pricing ||
    !details ||
    !formState.isValid;

  return (
    <Stack
      spacing={2}
      sx={{ flex: 1, pt: 1 }}
      component="form"
      noValidate
      onSubmit={handleSubmit(handleNext)}
    >
      <Stack spacing={2}>
        {displaySubscriptionPeriods && (
          <Grid container justifyContent="space-between">
            <Typography>{t('organizationPurchase.currentTerm')}</Typography>
            <Typography>
              {t('organizationPurchase.termRange', {
                start: formatDate(details.period.start, DATE_ONLY_FORMAT),
                end: formatDate(details.period.end, DATE_ONLY_FORMAT),
              })}
            </Typography>
          </Grid>
        )}

        {/* current seats */}
        <OrganizationSeatsSummary seats={details?.seats} />

        <Grid>
          <Typography variant="h2">
            {t('organizationPurchase.addMemberships')}
          </Typography>
        </Grid>

        {displaySubscriptionPeriods && (
          <Grid container justifyContent="space-between">
            <Typography>{t('organizationPurchase.period')}</Typography>
            <Typography fontWeight={700}>
              {t('organizationPurchase.termRange', {
                start: formatDate(currentDate, DATE_ONLY_FORMAT),
                end: formatDate(details.period.end, DATE_ONLY_FORMAT),
              })}
            </Typography>
          </Grid>
        )}

        <OrganizationSeatsFormFields
          control={control}
          formState={formState}
          isDisabled={formState.isSubmitting}
          pricing={pricing}
          showLearnMore
        />

        <OrganizationUpgradeSummary
          regularSeats={regularSeats}
          proSeats={proSeats}
        />
      </Stack>

      <Stack sx={{ flexGrow: 1, justifyContent: 'flex-end' }}>
        <Button size="large" fullWidth type="submit" disabled={isDisabled}>
          {t('organizationPurchase.continue')}
        </Button>
      </Stack>
    </Stack>
  );
};
